/* import { useState } from "react";
 */import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { Card, CardContent } from "../../ui/card";
import { useChartColors } from "../../../hooks/useChartColors";
import ManagerialLoader from "../../../components/loaders/ManagerialLoader";
import { SummaryHeader } from "../../ui/SummaryHeader";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";
import { TabsContent } from "@radix-ui/react-tabs";
import { useOverviewKpis } from "../hooks/useOverviewKpis";
import { DataTable } from "../../ui/data-table/data-table";
import { formatCurrency } from "../../../utils/formatCurrency";

interface CumulativeCollectionPerformanceChartProps {
    start_date: string;
    end_date: string;
}

interface CollectionPerformance {
    collection: string;
    shopify_revenue: number;
    wholesale_revenue: number;
    total_revenue: number;
}


export function CumulativeCollectionPerformanceChart({
  start_date,
  end_date,
}: CumulativeCollectionPerformanceChartProps) {
  const { data, isLoading } = useOverviewKpis({ start_date, end_date });
  const colors = useChartColors("managerial");

  if (!colors) return null;

  // Ensure this check only happens after loading is complete
  const cumulativePerformance = !isLoading ? data?.[1]?.cumulative_performance : null;

  if (!isLoading && !cumulativePerformance) {
    return <div>No cumulative performance data available.</div>;
  }

  

  const collectionData =
    cumulativePerformance?.collection_performance.map((item: CollectionPerformance) => ({
      name: item.collection,
      shopify: item.shopify_revenue,
      wholesale: item.wholesale_revenue,
      total: item.total_revenue,
    })) || [];
  const noData = collectionData.length === 0;

  const columns = [
    {
      accessorKey: "name",
      header: "Category",
    },
    {
      accessorKey: "shopify",
      header: "Shopify Revenue",
      isNumber: true,
      cell: (info: any) => formatCurrency(info.getValue()),
    },
    {
      accessorKey: "wholesale",
      header: "Wholesale Revenue",
      isNumber: true,
      cell: (info: any) => formatCurrency(info.getValue()),
    },
    {
      accessorKey: "total",
      header: "Total Revenue",
      isNumber: true,
      cell: (info: any) => formatCurrency(info.getValue()),
    },
  ];

  return (
    <>
      <SummaryHeader title="Collection" />
      <Card>
        <CardContent>
          <Tabs defaultValue="chart" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 items-center justify-between mb-4">
              <TabsList className="flex flex-wrap gap-1">
                <TabsTrigger value="chart">Chart</TabsTrigger>
                <TabsTrigger value="table">Table</TabsTrigger>
              </TabsList>
            </div>
            {/* Collection Performance */}
            <TabsContent
              value="chart"
              className="w-full h-full p-0 bg-transparent dark:bg-transparent rounded-none shadow-none-soft overflow-visible"
            >
              {isLoading ? (
                <ManagerialLoader variant="channel-perf" />
              ) : (
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={collectionData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="name"
                        className="text-xs sm:text-sm text-muted-foreground"
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        className="text-xs sm:text-sm text-muted-foreground"
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip
                        formatter={(value) => formatCurrency(Number(value))}
                        contentStyle={{
                          backgroundColor: "var(--background)",
                          borderColor: "var(--border)",
                          borderRadius: "var(--radius)",
                          fontSize: "0.875rem",
                        }}
                      />
                      <Legend />
                      <Bar dataKey="shopify" name="Shopify" stackId="a" fill={colors[0]} />
                      <Bar dataKey="wholesale" name="Wholesale" stackId="a" fill={colors[1]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </TabsContent>

            <TabsContent
              value="table"
              className="w-full h-full p-0 bg-transparent dark:bg-transparent rounded-none shadow-none-soft overflow-visible"
            >
              <div className="rounded-md border max-h-[550px] overflow-auto p-4">
                {isLoading ? (
                  <ManagerialLoader variant="channel-perf" />
                ) : noData ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No Data Available
                  </div>
                ) : (
                  <DataTable
                    columns={columns}
                    data={collectionData}
                    searchColumn="name"
                    searchPlaceholder="Search by Collection..."
                    showRowsPerPage={false}
                    initialPageSize={5}
                    showTotal={true} // Enable totals row
                    totalColumns={["total"]} // Columns to total
                  />
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </>
  );
}
